import { MonthRow } from '../../domain/types.js';
import { formatCompactRub, formatMonthLabel, formatPercent, formatRub } from '../format.js';
import {
  ChartMode,
  EMPTY_TEXT,
  LegendEntry,
  MODES,
  SURFACE,
  createFrame,
  drawXAxis,
  drawYAxis,
  estimateTextWidth,
  marker,
  renderEmpty,
} from './frame.js';
import { linearScale, niceTicks } from './scales.js';
import { areaPath, linePath, svgEl, uid } from './svg.js';
import { TooltipRow, attachCrosshair } from './tooltip.js';

export type PaymentChartInput = {
  months: MonthRow[];
  baselineMonths?: MonthRow[];
};

type Point = { x: number; y: number };

const SPIKE_RATIO = 1.5;

function totalOf(month: MonthRow): number {
  return month.scheduledPayment + month.overpayment;
}

/** Y-axis cap: one-off spikes above 1.5× the 95th percentile are clipped and marked with their value. */
function axisCap(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const max = sorted[sorted.length - 1] ?? 0;
  const p95 = sorted[Math.floor(0.95 * (sorted.length - 1))] ?? max;
  return max > p95 * SPIKE_RATIO && p95 > 0 ? p95 * SPIKE_RATIO : max;
}

function stackedArea(upper: Point[], lower: Point[], color: string, series: string): SVGPathElement {
  return svgEl('path', {
    d: areaPath(upper, lower),
    fill: color,
    'fill-opacity': 0.55,
    stroke: SURFACE,
    'stroke-width': 2,
    'stroke-linejoin': 'round',
    'data-series': series,
  });
}

export function render(container: HTMLElement, input: PaymentChartInput, mode: ChartMode = 'compact'): void {
  const { months } = input;
  const baseline = input.baselineMonths ?? [];
  const hasBaseline = baseline.length > 0;
  if (!months.length) {
    renderEmpty(container, EMPTY_TEXT);
    return;
  }

  const legend: LegendEntry[] = [
    { label: 'Проценты', color: 'var(--series-interest)', kind: 'area' },
    { label: 'Основной долг', color: 'var(--series-principal)', kind: 'area' },
    { label: 'Досрочный платёж', color: 'var(--series-overpayment)', kind: 'area' },
  ];
  if (hasBaseline) legend.push({ label: 'Платёж в базовом', color: 'var(--text-muted)', kind: 'dashed' });

  const totals = months.map(totalOf);
  const baseTotals = baseline.map(totalOf);
  const cap = axisCap([...totals, ...baseTotals]);
  const ticks = niceTicks(0, cap, MODES[mode].yTicks);
  const yMax = ticks[ticks.length - 1];

  const frame = createFrame(container, mode, {
    ariaLabel: 'Состав платежа',
    legend,
    yLabels: ticks.map(formatCompactRub),
  });
  const { svg, plot, fontSize } = frame;

  const maxLen = Math.max(months.length, baseline.length);
  const xScale = linearScale(0, Math.max(1, maxLen - 1), plot.x0, plot.x1);
  const yScale = linearScale(0, yMax, plot.y1, plot.y0);

  drawYAxis(frame, ticks, yScale, formatCompactRub);

  const clipId = uid('payment-clip');
  svg.append(svgEl('defs', {}, [
    svgEl('clipPath', { id: clipId }, [
      svgEl('rect', {
        x: plot.x0.toFixed(1),
        y: plot.y0.toFixed(1),
        width: (plot.x1 - plot.x0).toFixed(1),
        height: (plot.y1 - plot.y0).toFixed(1),
      }),
    ]),
  ]));

  const floor: Point[] = months.map((_, i) => ({ x: xScale(i), y: plot.y1 }));
  const interestTop: Point[] = months.map((m, i) => ({ x: xScale(i), y: yScale(m.interestPortion) }));
  const principalTop: Point[] = months.map((m, i) => ({ x: xScale(i), y: yScale(m.interestPortion + m.principalPortion) }));
  const totalTop: Point[] = months.map((_, i) => ({ x: xScale(i), y: yScale(totals[i]) }));

  const stack = svgEl('g', { class: 'chart-stack', 'clip-path': `url(#${clipId})` });
  stack.append(stackedArea(interestTop, floor, 'var(--series-interest)', 'interest'));
  stack.append(stackedArea(principalTop, interestTop, 'var(--series-principal)', 'principal'));
  stack.append(stackedArea(totalTop, principalTop, 'var(--series-overpayment)', 'overpayment'));
  if (hasBaseline) {
    stack.append(svgEl('path', {
      d: linePath(baseline.map((_, i) => ({ x: xScale(i), y: yScale(baseTotals[i]) }))),
      fill: 'none',
      stroke: 'var(--text-muted)',
      'stroke-width': 1.5,
      'stroke-dasharray': '4,3',
      'stroke-linejoin': 'round',
      'data-series': 'baseline',
    }));
  }
  svg.append(stack);

  // Clipped spikes get a small marker with the real value.
  const spikes = svgEl('g', { class: 'chart-spikes' });
  totals.forEach((total, i) => {
    if (total <= yMax) return;
    const x = xScale(i);
    const text = formatCompactRub(total);
    const fits = x + 6 + estimateTextWidth(text, fontSize) <= plot.x1;
    spikes.append(svgEl('polygon', {
      points: `${(x - 4).toFixed(1)},${(plot.y0 + 8).toFixed(1)} ${(x + 4).toFixed(1)},${(plot.y0 + 8).toFixed(1)} ${x.toFixed(1)},${plot.y0.toFixed(1)}`,
      fill: 'var(--text-muted)',
    }));
    spikes.append(svgEl('text', {
      x: (fits ? x + 6 : x - 6).toFixed(1),
      y: (plot.y0 + fontSize).toFixed(1),
      'text-anchor': fits ? 'start' : 'end',
      'font-size': fontSize,
      fill: 'var(--text-muted)',
    }, [text]));
  });
  svg.append(spikes);

  drawXAxis(frame, months.length >= baseline.length ? months : baseline, { xForIndex: xScale });

  const focus = marker(0, 0, 'var(--text-primary)');
  focus.setAttribute('visibility', 'hidden');
  svg.append(focus);

  attachCrosshair({
    svg,
    host: container,
    plot,
    count: maxLen,
    xForIndex: xScale,
    renderTooltip: (i): TooltipRow[] => {
      const month = months[i];
      const base = baseline[i];
      const rows: TooltipRow[] = [{ value: formatMonthLabel((month ?? base).dateLabel) }];
      if (month) {
        const total = totals[i];
        const share = total > 0 ? Math.round((month.interestPortion / total) * 1000) / 10 : 0;
        rows.push({ value: formatRub(total), label: 'Всего за месяц' });
        rows.push({ value: formatRub(month.interestPortion), label: 'Проценты', color: 'var(--series-interest)' });
        rows.push({ value: formatRub(month.principalPortion), label: 'Основной долг', color: 'var(--series-principal)' });
        rows.push({ value: month.overpayment > 0 ? formatRub(month.overpayment) : '—', label: 'Досрочный платёж', color: 'var(--series-overpayment)' });
        rows.push({ value: formatPercent(share, 1), label: 'Проценты в платеже' });
      } else {
        rows.push({ value: '—', label: 'Всего за месяц' });
      }
      if (hasBaseline) {
        rows.push({ value: base ? formatRub(baseTotals[i]) : '—', label: 'Всего в базовом', color: 'var(--text-muted)' });
      }
      return rows;
    },
    onIndex: i => {
      if (i === null || !months[i]) {
        focus.setAttribute('visibility', 'hidden');
        return;
      }
      focus.setAttribute('cx', xScale(i).toFixed(1));
      focus.setAttribute('cy', yScale(Math.min(totals[i], yMax)).toFixed(1));
      focus.setAttribute('visibility', 'visible');
    },
  });
}
